import { FC, useEffect } from 'react'
import styled from 'styled-components'
import PageContentWrapper from '@/components/organisms/pageContentWrapper'
import { validFor } from '@/theme/validFor'
import { Box, Flex } from '@/components/atoms/Grid'
import { Body, H2 } from '@/components/atoms/typography'
import PostDetailSocialBlock from '@/components/organisms/blog/postDetailSocialBlock'
import PostDetailAboutAuthor from '@/components/organisms/blog/postDetailAboutAuthor'
import PostDetailSlider from '@/components/organisms/blog/postDetailSlider'
import BlogPosts from '@/components/organisms/blog/blogPosts'
import BlogNewsletterForm from '@/components/organisms/blog/blogNewsletterForm'
import { withBaseServerSideProps } from '@/HOC/withBaseServerSideProps'
import { requestThrow401 } from '@/utils/graphQl'
import { getMainData } from '@/utils/mainDataHelper'
import { Blog, Page } from '@/network/graphql.g'
import { DateTime } from 'luxon'
import ResponsiveImage from '@/components/atoms/responsiveImage'
import { useMhub } from '@/providers/mhubProvider'
import useDevice from 'hooks/useDevice'

type PostDetailProps = {
  blogs: Blog[]
  blog: Blog
}

enum ProductFilterType {
  Campaign = '/campaign/',
  Category = 'category/'
}

const PostDetail: FC<PostDetailProps> = ({ blogs, blog }) => {
  const {
    title,
    description,
    desktopImage,
    mobileImage,
    author,
    productFilter
  } = blog
  const { isMobile } = useDevice()
  const createdAt = DateTime.fromISO(blog.createdAt).toFormat('dd.MM.yyyy')
  const { pushBlogDetailPage } = useMhub()
  useEffect(() => {
    pushBlogDetailPage(blog)
  }, [blog])

  const alternativeImage = (e) => {
    e.target.src = 'https://i.factcool.com/summer/landing-page-summer_11.jpg'
  }

  const isCampaign = productFilter?.startsWith(ProductFilterType.Campaign)
  const isCategory = productFilter?.startsWith(ProductFilterType.Category)

  return (
    <StyledPageContentWrapper>
      <StyledFlex alignItems="center" flexDirection="column">
        <MainTitle mt={2} mb={2}>
          {title}
        </MainTitle>
        <ImageWrapper mt={3} mb={4}>
          <ResponsiveImage
            imageUrl={isMobile ? mobileImage : desktopImage}
            alt="Fashion Blog"
            width={1000}
            height={450}
            objectFit="cover"
            onError={alternativeImage}
          />
          <DateBox>
            <Body>{createdAt}</Body>
          </DateBox>
        </ImageWrapper>
        <Box mb={2} dangerouslySetInnerHTML={{ __html: description }} />
        <PostDetailSocialBlock />
        <PostDetailAboutAuthor author={author} />
        {productFilter ? (
          <PostDetailSlider
            productFilter={productFilter}
            isCampaign={isCampaign}
            isCategory={isCategory}
          />
        ) : null}
        <BlogPosts blogPosts={blogs} blog={blog} />
        <BlogNewsletterForm />
      </StyledFlex>
    </StyledPageContentWrapper>
  )
}

export default PostDetail

const StyledPageContentWrapper = styled(PageContentWrapper)`
  max-width: 1000px;
  margin: 0 auto;
  padding-block: 72px;
  ${validFor.mobile` 
    padding: 0 0 56px; 
	`};
`

const StyledFlex = styled(Flex)`
  ${validFor.mobile` 
    margin-inline: 28px;
    padding-inline: 0;
	`};
`

const MainTitle = styled(H2)`
  font-size: 28px;
  ${validFor.mobile` 
    font-size: 20px;
    line-height: 1.4;
    margin-top: 28px;
	`};
`

const ImageWrapper = styled(Box)`
  position: relative;
  width: 100%;
`

const DateBox = styled(Box)`
  position: absolute;
  box-sizing: border-box;
  width: 120px;
  padding: 4px 6px;
  text-align: center;
  background-color: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.onBackground};
  left: calc(50% - 60px);
  bottom: -13px;
  & > p {
    margin: 0;
    line-height: 1;
  }
`

export const getServerSideProps = withBaseServerSideProps(
  async ({
    context,
    sdk,
    withMainNavigation,
    withStore
  }): Promise<GetServerSideProps<PostDetailProps>> => {
    const { success: blogDetailSuccess, response: blogDetailResponse } =
      await requestThrow401({
        method: sdk.getBlogDetail,
        variables: {
          slug: context.params.slug as string,
          withMainNavigation,
          withStore
        },
        mainResponse: (response) => response?.blog
      })
    const { success: blogSuccess, response: blogsResponse } =
      await requestThrow401({
        method: sdk.getBlogData,
        variables: { page: Page.Blogs, withMainNavigation, withStore },
        mainResponse: (response) => response?.blogs
      })
    if (!blogDetailSuccess || !blogSuccess || !blogDetailResponse?.blog) {
      return {
        notFound: true
      }
    }

    return {
      props: {
        mainData: getMainData(blogDetailResponse),
        seoTags: blogDetailResponse.seoTags,
        blogs: blogsResponse?.blogs,
        blog: blogDetailResponse.blog
      }
    }
  }
)
